import { useState, useEffect, useCallback } from 'react';
import type { DashboardPreference } from '../types';

const STORAGE_KEY = 'amashr_dashboard_preferences';

interface UseDashboardPreferenceOptions {
  userId: string;
  tenantSlug?: string;
  onPreferenceChange?: (preference: DashboardPreference) => Promise<void>;
}

interface UseDashboardPreferenceReturn {
  preferredDashboardId: string | null;
  setPreferredDashboard: (dashboardId: string) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook for managing user's preferred dashboard
 */
export const useDashboardPreference = ({
  userId,
  tenantSlug,
  onPreferenceChange
}: UseDashboardPreferenceOptions): UseDashboardPreferenceReturn => {
  const [preferredDashboardId, setPreferredDashboardId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Generate storage key based on user and tenant
  const getStorageKey = useCallback(() => {
    const key = tenantSlug 
      ? `${STORAGE_KEY}_${userId}_${tenantSlug}`
      : `${STORAGE_KEY}_${userId}`;
    return key;
  }, [userId, tenantSlug]);

  // Load preference from localStorage
  useEffect(() => {
    const loadPreference = () => {
      try {
        const key = getStorageKey();
        const stored = localStorage.getItem(key);
        
        if (stored) {
          const preference: DashboardPreference = JSON.parse(stored);
          setPreferredDashboardId(preference.preferredDashboardId);
        }
      } catch (err) {
        console.error('Failed to load dashboard preference:', err);
        setError(err as Error);
      }
    };

    loadPreference();
  }, [getStorageKey]);

  // Set preferred dashboard
  const setPreferredDashboard = useCallback(async (dashboardId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const preference: DashboardPreference = {
        userId,
        tenantSlug,
        preferredDashboardId: dashboardId,
        lastUpdated: new Date().toISOString()
      };

      // Save to localStorage
      const key = getStorageKey();
      localStorage.setItem(key, JSON.stringify(preference));
      setPreferredDashboardId(dashboardId);

      // Call API if handler provided
      if (onPreferenceChange) {
        await onPreferenceChange(preference);
      }
    } catch (err) {
      console.error('Failed to save dashboard preference:', err);
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [userId, tenantSlug, getStorageKey, onPreferenceChange]);

  return {
    preferredDashboardId,
    setPreferredDashboard,
    isLoading,
    error
  };
};

/**
 * API handler for dashboard preference (to be implemented with actual API)
 */
export const createDashboardPreferenceApiHandler = (apiBaseUrl: string) => {
  return async (preference: DashboardPreference): Promise<void> => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/me/preferences/dashboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(preference),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to save preference: ${response.statusText}`);
      }
    } catch (error) {
      // Log error but don't throw - localStorage is the primary storage
      console.warn('API preference save failed, using localStorage only:', error);
    }
  };
};